/**
 * Запросы районной вкладки «Цифрового паспорта» (US-12, задача DP-417) —
 * шесть операций `/api/public/district/*` + `/api/public/cpi`.
 *
 * Одна операция на блок, а не одна на экран: бюджет приходит месячным
 * срезом казначейской формы, отраслевая статистика — бюллетенями разных
 * дней, населённые пункты — годовым срезом, административное деление
 * меняется «по мере изменений». Общая операция задерживала бы вкладку
 * ради самого медленного источника, а её сбой обнулял бы наполненные
 * блоки (US-12 FR-1.1).
 *
 * Обязателен только `kato`; без периода бэкенд отдаёт последний доступный
 * и помечает его `period.is_current = false`.
 *
 * Нормализаторы держат контракт: неизвестный `status` трактуется как
 * `NO_DATA` (показать блок пустым безопаснее, чем скрыть), мусор в числах
 * — `null`, а не 0 (US-12 §2: `null` ≠ `0`).
 */
import { api } from './api';
import type {
  CpiCategory,
  CpiResponse,
  DistrictAdminDivisionResponse,
  DistrictBlockMeta,
  DistrictBlockStatus,
  DistrictBudgetResponse,
  DistrictBudgetRow,
  DistrictPeriod,
  DistrictPopulationResponse,
  DistrictSerIndustry,
  DistrictSerResponse,
  DistrictSettlement,
  DistrictSettlementsResponse,
  DivisionCounterKey,
} from '../types/district-passport';

// --- примитивы нормализации (как в public.request.ts) ---

const asObj = (v: unknown): Record<string, unknown> =>
  typeof v === 'object' && v !== null ? (v as Record<string, unknown>) : {};

const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);

const num = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;

const int = (v: unknown): number | null => {
  const value = num(v);
  return value === null ? null : Math.trunc(value);
};

const str = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v : null;

const bool = (v: unknown): boolean => v === true;

const STATUSES = [
  'OK',
  'NO_DATA',
  'NOT_APPLICABLE',
  'LEVEL_NOT_SUPPORTED',
  'DISABLED',
] as const;

/**
 * Состояние блока. Неизвестное значение — `NO_DATA`, а не `DISABLED`:
 * ошибочно скрытый блок выглядит как потерянный виджет, ошибочно пустой —
 * как ненаполненный, и второе честнее.
 */
const status = (raw: unknown): DistrictBlockStatus =>
  typeof raw === 'string' && (STATUSES as readonly string[]).includes(raw)
    ? (raw as DistrictBlockStatus)
    : 'NO_DATA';

const period = (raw: unknown): DistrictPeriod | null => {
  if (raw === null || raw === undefined) return null;
  const obj = asObj(raw);
  return {
    reporting_date: str(obj.reporting_date),
    period_start: str(obj.period_start),
    period_end: str(obj.period_end),
    label_ru: str(obj.label_ru),
    label_kz: str(obj.label_kz),
    is_current: obj.is_current !== false,
  };
};

const meta = (root: Record<string, unknown>): DistrictBlockMeta => ({
  kato: int(root.kato),
  module_id: str(root.module_id),
  status: status(root.status),
  period: period(root.period),
});

// --- нормализаторы блоков ---

const DIVISION_COUNTER_KEYS: readonly string[] = [
  'districts',
  'oblast_cities',
  'cities',
  'okrugs',
  'villages',
];

export const normalizeAdminDivision = (
  raw: unknown,
): DistrictAdminDivisionResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    // Неприменимая категория приходит null — счётчик скрывается, нуль
    // показал бы её как факт (US-12 FR-5.2).
    rural_district_count: int(root.rural_district_count),
    village_count: int(root.village_count),
    city_count: int(root.city_count),
    district_count: int(root.district_count),
    oblast_city_count: int(root.oblast_city_count),
    // Неизвестный ключ пропускается: счётчика, которого экран не знает,
    // он и не нарисует.
    counters: (Array.isArray(root.counters) ? root.counters : []).filter(
      (key): key is DivisionCounterKey =>
        typeof key === 'string' && DIVISION_COUNTER_KEYS.includes(key),
    ),
    valid_from: str(root.valid_from),
    as_of_date: str(root.as_of_date),
  };
};

export const normalizeDistrictPopulation = (
  raw: unknown,
): DistrictPopulationResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    unit_ru: str(root.unit_ru),
    unit_kz: str(root.unit_kz),
    total: int(root.total),
    urban: int(root.urban),
    rural: int(root.rural),
    methodology: str(root.methodology),
    methodology_note_ru: str(root.methodology_note_ru),
    methodology_note_kz: str(root.methodology_note_kz),
    is_refined: typeof root.is_refined === 'boolean' ? root.is_refined : null,
  };
};

export const normalizeSettlements = (
  raw: unknown,
): DistrictSettlementsResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    unit_ru: str(root.unit_ru),
    unit_kz: str(root.unit_kz),
    // Официальный итог из источника; суммой items он не подменяется
    // (US-12 FR-7.3).
    total: int(root.total),
    items: asArr(root.items).flatMap<DistrictSettlement>((item) => {
      const row = asObj(item);
      const name = str(row.name_ru) ?? str(row.name_kz);
      if (name === null) return [];
      return [
        {
          kato: int(row.kato),
          name_ru: str(row.name_ru),
          name_kz: str(row.name_kz),
          population: int(row.population),
          is_town: bool(row.is_town),
        },
      ];
    }),
  };
};

const serIndustry = (raw: unknown): DistrictSerIndustry => {
  const row = asObj(raw);
  return {
    key: str(row.key),
    name_ru: str(row.name_ru),
    name_kz: str(row.name_kz),
    // Объём уже приведён платформой к volume_unit_* — экран не делит и не
    // умножает: ошибка в 1000 раз выглядит правдоподобно (US-12 FR-3.1).
    volume: num(row.volume),
    volume_unit_ru: str(row.volume_unit_ru),
    volume_unit_kz: str(row.volume_unit_kz),
    cumulative: row.cumulative !== false,
    // ИФО как есть, в процентах к периоду прошлого года (110,6, а не 10,6).
    ifo_percent: num(row.ifo_percent),
    ifo_basis_ru: str(row.ifo_basis_ru),
    ifo_basis_kz: str(row.ifo_basis_kz),
    period: period(row.period),
    drill_through_available: bool(row.drill_through_available),
    analytics_section: str(row.analytics_section),
  };
};

export const normalizeDistrictSer = (raw: unknown): DistrictSerResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    // Порядок отраслей задаёт ответ (FR-8.3): экран не сортирует и строку
    // без значений не выбрасывает — четыре отрасли видны всегда.
    industries: asArr(root.industries).map(serIndustry),
  };
};

const budgetRow = (raw: unknown): DistrictBudgetRow => {
  const row = asObj(raw);
  return {
    key: str(row.key),
    name_ru: str(row.name_ru),
    name_kz: str(row.name_kz),
    level: int(row.level),
    status: status(row.status),
    plan_yearly: num(row.plan_yearly),
    plan_period: num(row.plan_period),
    fact_period: num(row.fact_period),
    // Две метрики исполнения не сливаются в одну (FR-9.2).
    period_plan_execution_pct: num(row.period_plan_execution_pct),
    annual_plan_utilization_pct: num(row.annual_plan_utilization_pct),
  };
};

export const normalizeDistrictBudget = (
  raw: unknown,
): DistrictBudgetResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    unit_ru: str(root.unit_ru),
    unit_kz: str(root.unit_kz),
    cumulative: root.cumulative !== false,
    calculation_rule_ru: str(root.calculation_rule_ru),
    calculation_rule_kz: str(root.calculation_rule_kz),
    // Иерархия строк — из ответа (level), а не из названия (FR-9.1).
    rows: asArr(root.rows).flatMap<DistrictBudgetRow>((item) => {
      const row = budgetRow(item);
      return row.key === null && row.name_ru === null ? [] : [row];
    }),
  };
};

export const normalizeCpi = (raw: unknown): CpiResponse => {
  const root = asObj(raw);
  return {
    ...meta(root),
    // Уровень, к которому ОТНОСИТСЯ индекс: по району приходит областной,
    // и пометка «по области» ставится по этим полям (FR-11.3).
    source_kato: int(root.source_kato),
    territory_level: str(root.territory_level),
    basis_ru: str(root.basis_ru),
    basis_kz: str(root.basis_kz),
    categories: asArr(root.categories).flatMap<CpiCategory>((item) => {
      const row = asObj(item);
      const key = str(row.key);
      if (key === null) return [];
      return [
        {
          key,
          name_ru: str(row.name_ru),
          name_kz: str(row.name_kz),
          value: num(row.value),
        },
      ];
    }),
  };
};

// --- запросы ---

const request = async <T>(
  path: string,
  normalize: (raw: unknown) => T,
  params: Record<string, number>,
): Promise<T> => {
  const response = await api.get<unknown>(path, { params });
  return normalize(response.data);
};

/** Параметры периода: год и месяц уходят только если заданы. */
const withPeriod = (
  kato: number,
  year?: number | null,
  month?: number | null,
): Record<string, number> => {
  const params: Record<string, number> = { kato };
  if (year != null) params.year = year;
  if (month != null) params.month = month;
  return params;
};

export const getDistrictAdminDivision = (
  kato: number,
): Promise<DistrictAdminDivisionResponse> =>
  request('/public/district/administrative-division', normalizeAdminDivision, {
    kato,
  });

export const getDistrictPopulation = (
  kato: number,
  year?: number | null,
  month?: number | null,
): Promise<DistrictPopulationResponse> =>
  request(
    '/public/district/population',
    normalizeDistrictPopulation,
    withPeriod(kato, year, month),
  );

/** Разрез годовой: месяц операция не принимает (US-12 FR-7.5). */
export const getDistrictSettlements = (
  kato: number,
  year?: number | null,
): Promise<DistrictSettlementsResponse> =>
  request(
    '/public/district/settlement-population',
    normalizeSettlements,
    withPeriod(kato, year),
  );

export const getDistrictSer = (
  kato: number,
  year?: number | null,
  month?: number | null,
): Promise<DistrictSerResponse> =>
  request(
    '/public/district/ser',
    normalizeDistrictSer,
    withPeriod(kato, year, month),
  );

export const getDistrictBudget = (
  kato: number,
  year?: number | null,
  month?: number | null,
): Promise<DistrictBudgetResponse> =>
  request(
    '/public/district/budget',
    normalizeDistrictBudget,
    withPeriod(kato, year, month),
  );

/** ИПЦ применим и к уровню области — блок есть на обеих вкладках. */
export const getCpi = (
  kato: number,
  year?: number | null,
  month?: number | null,
): Promise<CpiResponse> =>
  request('/public/cpi', normalizeCpi, withPeriod(kato, year, month));
